import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * @typedef {{ key: string, label?: string }} IExportColumn
 */

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(format: 'excel' | 'pdf', selectedColumns?: string[]) => void | Promise<void>} props.onExport
 * @param {boolean} [props.loading]
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {(string | IExportColumn)[]} [props.pdfColumns] - when set, shown as multiselect after PDF is chosen
 * @param {string[]} [props.defaultUnselectedPdfColumns] - column keys left unchecked when the dialog opens
 * @param {string} [props['data-testid']]
 */
export function ExportFormatDialog({
  open,
  onOpenChange,
  onExport,
  loading = false,
  title = 'Export',
  description = 'Choose a file format to download.',
  pdfColumns,
  defaultUnselectedPdfColumns = [],
  'data-testid': testId = 'export-format-dialog',
}) {
  const [format, setFormat] = useState('excel');
  const [selectedColumns, setSelectedColumns] = useState([]);

  const normalizedColumns = useMemo(() => {
    if (!Array.isArray(pdfColumns)) return [];
    return pdfColumns
      .map((col) => {
        if (typeof col === 'string') return { key: col, label: col };
        if (col?.key) return { key: col.key, label: col.label || col.key };
        return null;
      })
      .filter(Boolean);
  }, [pdfColumns]);

  const allKeys = useMemo(
    () => normalizedColumns.map((c) => c.key),
    [normalizedColumns]
  );

  const initiallySelectedKeys = useMemo(() => {
    const skip = new Set(
      Array.isArray(defaultUnselectedPdfColumns) ? defaultUnselectedPdfColumns : []
    );
    return allKeys.filter((key) => !skip.has(key));
  }, [allKeys, defaultUnselectedPdfColumns]);

  useEffect(() => {
    if (!open) return;
    setFormat('excel');
    setSelectedColumns(initiallySelectedKeys);
  }, [open, initiallySelectedKeys]);

  const showColumnPicker = format === 'pdf' && normalizedColumns.length > 0;

  const handleToggleColumn = (key) => {
    setSelectedColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleSelectAll = () => setSelectedColumns(allKeys);
  const handleClearAll = () => setSelectedColumns([]);

  const handleExport = async () => {
    if (format === 'pdf' && normalizedColumns.length > 0 && selectedColumns.length === 0) {
      return;
    }
    const orderedSelected = allKeys.filter((key) => selectedColumns.includes(key));
    await onExport?.(format, format === 'pdf' ? orderedSelected : undefined);
  };

  const canDownload =
    !loading &&
    (format === 'excel' || !normalizedColumns.length || selectedColumns.length > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="font-[Manrope]">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          <button
            type="button"
            disabled={loading}
            onClick={() => setFormat('excel')}
            data-testid={`${testId}-excel`}
            className={cn(
              'flex flex-col items-center gap-2 rounded-lg border p-4 text-sm transition-colors',
              format === 'excel'
                ? 'border-[#0047AB] bg-[#0047AB]/5 ring-1 ring-[#0047AB]'
                : 'border-border hover:bg-muted/50'
            )}
          >
            <FileSpreadsheet
              className={cn(
                'w-8 h-8',
                format === 'excel' ? 'text-[#0047AB]' : 'text-muted-foreground'
              )}
            />
            <span className="font-medium">Excel</span>
            <span className="text-xs text-muted-foreground">.xlsx spreadsheet</span>
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={() => setFormat('pdf')}
            data-testid={`${testId}-pdf`}
            className={cn(
              'flex flex-col items-center gap-2 rounded-lg border p-4 text-sm transition-colors',
              format === 'pdf'
                ? 'border-[#0047AB] bg-[#0047AB]/5 ring-1 ring-[#0047AB]'
                : 'border-border hover:bg-muted/50'
            )}
          >
            <FileText
              className={cn(
                'w-8 h-8',
                format === 'pdf' ? 'text-[#0047AB]' : 'text-muted-foreground'
              )}
            />
            <span className="font-medium">PDF</span>
            <span className="text-xs text-muted-foreground">.pdf document</span>
          </button>
        </div>

        {showColumnPicker && (
          <div className="space-y-2" data-testid={`${testId}-columns`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs uppercase tracking-widest font-medium text-muted-foreground">
                PDF Columns
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="text-xs text-[#0047AB] hover:underline disabled:opacity-50"
                  disabled={loading || selectedColumns.length === allKeys.length}
                  onClick={handleSelectAll}
                  data-testid={`${testId}-select-all`}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:underline disabled:opacity-50"
                  disabled={loading || selectedColumns.length === 0}
                  onClick={handleClearAll}
                  data-testid={`${testId}-clear-all`}
                >
                  Clear
                </button>
              </div>
            </div>

            <div
              className="max-h-48 overflow-y-auto overscroll-contain rounded-md border border-border p-1"
              role="listbox"
              aria-label="PDF columns"
              data-testid={`${testId}-columns-list`}
              onWheel={(e) => e.stopPropagation()}
            >
              {normalizedColumns.map((col) => {
                const checked = selectedColumns.includes(col.key);
                const id = `${testId}-col-${col.key}`;
                return (
                  <div
                    key={col.key}
                    className="flex items-center gap-2 rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground sm:py-1.5"
                  >
                    <Checkbox
                      id={id}
                      checked={checked}
                      onCheckedChange={() => handleToggleColumn(col.key)}
                      data-testid={`${testId}-checkbox-${col.key}`}
                    />
                    <label htmlFor={id} className="flex-1 cursor-pointer select-none leading-none">
                      {col.label}
                    </label>
                  </div>
                );
              })}
            </div>

            {selectedColumns.length === 0 && (
              <p className="text-xs text-red-600">Select at least one column for PDF.</p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            disabled={loading}
            onClick={() => onOpenChange?.(false)}
            data-testid={`${testId}-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canDownload}
            onClick={handleExport}
            className="bg-[#0047AB] hover:bg-[#003d96] text-white"
            data-testid={`${testId}-confirm`}
          >
            {loading ? 'Exporting...' : `Download ${format === 'pdf' ? 'PDF' : 'Excel'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ExportFormatDialog;
